/**
 * RealtimeService - Subscribes the widget to realtime_hub and dispatches
 * scoped events (cart.*, order.*, chat.*, handoff.*) to per-type listeners.
 *
 * The service opens a single WebSocket against realtime_hub, sends one
 * `subscribe` frame per event type on open, and routes incoming frames
 * to listeners registered via `on(eventType, callback)`. It auto-
 * reconnects with exponential backoff on abnormal close, sends a `ping`
 * heartbeat every 25s, and forces a reconnect if no `pong` arrives
 * within 30s.
 *
 * Auth: realtime_hub accepts a conversation-scoped JWT under `token`.
 * Public widget keys are not realtime credentials; `connect()` refuses
 * to open a socket without a non-widget-key token.
 *
 * Tenant isolation: `filters.organization_id` (and `cart_id`) MUST be
 * supplied. If either is missing, `connect()` skips the WS attempt
 * and logs a warning — better to be silent than to subscribe widely.
 */

import { RealtimeEvent } from '../types';

/** Subscribe frame shape (matches realtime_hub `subscribe` handler). */
export interface RealtimeSubscribeFilters {
  organization_id: string;
  conversation_id?: string;
  cart_id: string;
  [key: string]: string | undefined;
}

export interface RealtimeServiceConfig {
  /** Hub WS URL. Defaults to wss://realtime-hub.lumiari.ai/ws. */
  url?: string;
  /** Event types to subscribe to. */
  events: string[];
  /** Tenant + cart scope. organization_id and cart_id are required. */
  filters: RealtimeSubscribeFilters;
  /** Conversation-scoped JWT for authenticated subscriptions. */
  token?: string | null;
  /** Optional WebSocket factory — used by tests to inject a mock. */
  webSocketFactory?: (url: string) => WebSocket;
  /** Logger hook (defaults to console). Useful for silencing in tests. */
  logger?: Pick<Console, 'warn' | 'error' | 'log'>;
}

type Listener = (event: RealtimeEvent) => void;

const DEFAULT_URL = 'wss://realtime-hub.lumiari.ai/ws';
const HEARTBEAT_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 30_000;
const BACKOFF_STEPS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

export class RealtimeService {
  private url: string;
  private events: string[];
  private filters: RealtimeSubscribeFilters;
  private token: string | null;
  private factory: (url: string) => WebSocket;
  private logger: Pick<Console, 'warn' | 'error' | 'log'>;

  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private closed = false;
  private connectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private skipped = false;

  /**
   * Per-conversation last-seen sequence for `conversation.token_chunk`
   * frames. Out-of-order chunks (sequence <= lastSeq for a given
   * conversation_id) are dropped silently — over a flaky connection we
   * prefer a missing chunk over a duplicated one. Sequences reset on
   * `conversation.message_complete`.
   */
  private lastTokenSeq = new Map<string, number>();

  constructor(config: RealtimeServiceConfig) {
    this.url = config.url || DEFAULT_URL;
    this.events = config.events.slice();
    this.filters = config.filters;
    this.token = config.token || null;
    this.factory = config.webSocketFactory || ((u) => new WebSocket(u));
    this.logger = config.logger || console;
  }

  /**
   * Open the WS and queue the subscribe frame for `open`. Returns
   * immediately; results arrive asynchronously through listeners.
   * If filters are missing required tenant scope, this logs a warning
   * and is a no-op — caller is expected to gate on this.
   */
  connect(): void {
    if (this.closed) return;
    if (!this.filters || !this.filters.organization_id || !this.filters.cart_id) {
      this.skipped = true;
      this.logger.warn(
        '[RealtimeService] missing organization_id or cart_id; skipping subscription',
      );
      return;
    }
    if (!this.token || /^wk_/.test(this.token)) {
      this.skipped = true;
      this.logger.warn(
        '[RealtimeService] missing realtime JWT; skipping subscription',
      );
      return;
    }
    if (this.ws) return;
    this.openSocket();
  }

  /** Register a listener for a single event type. */
  on(eventType: string, callback: Listener): void {
    let bucket = this.listeners.get(eventType);
    if (!bucket) {
      bucket = new Set();
      this.listeners.set(eventType, bucket);
    }
    bucket.add(callback);
  }

  /** Remove a previously registered listener. */
  off(eventType: string, callback: Listener): void {
    const bucket = this.listeners.get(eventType);
    if (!bucket) return;
    bucket.delete(callback);
    if (bucket.size === 0) this.listeners.delete(eventType);
  }

  /**
   * Permanently shut the service down. Cancels reconnect, stops the
   * heartbeat, and closes the underlying socket. The instance can no
   * longer be reused after this — create a fresh one.
   */
  close(): void {
    this.closed = true;
    this.clearTimers();
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_e) {
        // ignore — socket may already be in CLOSING/CLOSED.
      }
      this.ws = null;
    }
  }

  /** Test/diag helper: true if we explicitly skipped due to bad scope. */
  isSkipped(): boolean {
    return this.skipped;
  }

  // ---- internal -----------------------------------------------------

  private openSocket(): void {
    let socket: WebSocket;
    try {
      socket = this.factory(this.url);
    } catch (e) {
      this.logger.error('[RealtimeService] WS construction failed', e);
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    const onOpen = () => {
      this.connectAttempt = 0;
      this.sendSubscribe();
      this.startHeartbeat();
    };
    const onMessage = (ev: MessageEvent) => this.handleMessage(ev);
    const onClose = () => this.handleClose();
    const onError = (ev: Event) => {
      this.logger.warn('[RealtimeService] socket error', ev);
    };

    if (typeof socket.addEventListener === 'function') {
      socket.addEventListener('open', onOpen);
      socket.addEventListener('message', onMessage);
      socket.addEventListener('close', onClose);
      socket.addEventListener('error', onError);
    } else {
      // Some legacy environments expose only on* properties.
      (socket as any).onopen = onOpen;
      (socket as any).onmessage = onMessage;
      (socket as any).onclose = onClose;
      (socket as any).onerror = onError;
    }
  }

  private sendSubscribe(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const payload = {
      type: 'subscribe',
      events: this.events,
      filters: this.filters,
      token: this.token,
    };
    try {
      this.ws.send(JSON.stringify(payload));
    } catch (e) {
      this.logger.error('[RealtimeService] subscribe send failed', e);
    }
  }

  private handleMessage(ev: MessageEvent): void {
    let parsed: { type?: string; data?: unknown } | null = null;
    try {
      parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data));
    } catch (e) {
      this.logger.warn('[RealtimeService] non-JSON frame dropped', e);
      return;
    }
    if (!parsed || typeof parsed.type !== 'string') return;

    if (parsed.type === 'pong') {
      this.clearPongTimer();
      return;
    }
    if (parsed.type === 'subscribed' || parsed.type === 'connected' || parsed.type === 'error') {
      // Control frames — useful for logs but no listener routing.
      return;
    }

    // Drop out-of-order LLM streaming chunks. Each chunk carries a
    // monotonic `sequence` per conversation_id. We compare against the
    // last sequence we saw and skip anything stale (network reorder,
    // duplicate retransmit). `conversation.message_complete` resets the
    // counter so the next stream starts clean.
    if (parsed.type === 'conversation.token_chunk') {
      const data = (parsed.data ?? {}) as {
        conversation_id?: string;
        sequence?: number;
      };
      const conv = String(data.conversation_id || '');
      const seq = typeof data.sequence === 'number' ? data.sequence : 0;
      if (conv) {
        const prev = this.lastTokenSeq.get(conv) ?? 0;
        if (seq <= prev) {
          // Stale or duplicate; drop silently.
          return;
        }
        this.lastTokenSeq.set(conv, seq);
      }
    } else if (parsed.type === 'conversation.message_complete') {
      const data = (parsed.data ?? {}) as { conversation_id?: string };
      const conv = String(data.conversation_id || '');
      if (conv) this.lastTokenSeq.delete(conv);
    }

    const event: RealtimeEvent = {
      type: parsed.type,
      data: parsed.data ?? parsed,
      received_at: Date.now(),
    };
    const bucket = this.listeners.get(parsed.type);
    if (!bucket) return;
    bucket.forEach((cb) => {
      try {
        cb(event);
      } catch (e) {
        this.logger.error('[RealtimeService] listener threw', e);
      }
    });
  }

  private handleClose(): void {
    this.stopHeartbeat();
    this.ws = null;
    if (this.closed) return;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    if (this.reconnectTimer) return;
    const delay =
      BACKOFF_STEPS_MS[Math.min(this.connectAttempt, BACKOFF_STEPS_MS.length - 1)];
    this.connectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closed) return;
      this.openSocket();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.sendPing(), HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.clearPongTimer();
  }

  private sendPing(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    } catch (e) {
      this.logger.warn('[RealtimeService] ping send failed', e);
      this.forceReconnect();
      return;
    }
    // Arm the pong watchdog if not already armed. We do NOT reset an
    // existing watchdog on every ping — that would let a flaky server
    // hide a real silence by keeping the timer perpetually rescheduled.
    if (this.pongTimer) return;
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      this.logger.warn('[RealtimeService] pong timeout — forcing reconnect');
      this.forceReconnect();
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private clearPongTimer(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private forceReconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_e) {
        // ignore
      }
      this.ws = null;
    }
    this.scheduleReconnect();
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
  }
}
